import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from '../../utils/translation';
import Disk from '../../icons/disk';
import { DEVICE_SIZE, mq } from '../../utils/mq';

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.div`
  align-items: center;
  border-bottom: solid 1px #dddddd;
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
`;

const ListItemLabel = styled.p`
  font-size: 18px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 20px;
    `,
  )}
`;

const ListItemCtaButton = styled.button`
  background-color: var(--theme-palette-green);
  border: none;
  border-radius: 6px;
  color: white;
  padding: 7px 8px;
  position: relative;
  text-align: center;
  transition: background-color 0.3s;

  &:hover {
    background-color: var(--theme-palette-dark-green);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      padding: 15px 25px;
    `,
  )}
`;

const ListItemCtaA = styled.a`
  background-color: var(--theme-palette-green);
  border: none;
  border-radius: 6px;
  color: white;
  padding: 7px 8px;
  position: relative;
  text-align: center;
  transition: background-color 0.3s;
  text-decoration: none;

  &:hover {
    background-color: var(--theme-palette-dark-green);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      padding: 15px 25px;
    `,
  )}
`;

const ListItemCtaTitle = styled.div`
  display: none;
  font-family: var(--theme-typography-primary-font);
  font-size: 18px;
  font-weight: bold;
  line-height: 1.38;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: block;
    `,
  )}
`;

const ListItemCtaVersion = styled.div`
  display: none;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.38;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: block;
    `,
  )}
`;

const ListItemCtaIcon = styled.div`
  display: block;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

type File = {
  os: string;
  onClick?: () => void;
  a?: {
    href: string;
    rel: string;
  };
};

export type FilesProps = {
  software: string;
  files: File[];
};

export type FileProps = Partial<File>;

const ListItemCta = ({ os, onClick, children, a }: React.PropsWithChildren<FileProps>) => {
  const t = useTranslation();

  if (a?.href) {
    return (
      <ListItemCtaA
        className="mod-green"
        href={a.href}
        rel={a.rel}
        aria-label={t('design.download.downloadFor', { os })}
      >
        {children}
      </ListItemCtaA>
    );
  }

  return (
    <ListItemCtaButton className="mod-green" onClick={onClick} aria-label={t('design.download.downloadFor', { os })}>
      {children}
    </ListItemCtaButton>
  );
};

export const OtherFiles = ({ files, software }: React.PropsWithChildren<FilesProps>) => {
  const t = useTranslation();

  return files?.slice(1)?.length ? (
    <List>
      {files.slice(1).map((file: File, index) => {
        return (
          <ListItem key={index}>
            <ListItemLabel>
              <strong>{software}</strong> {t('design.download.for')} {file.os}
            </ListItemLabel>
            <ListItemCta {...file}>
              <ListItemCtaTitle>{t('design.download.download')}</ListItemCtaTitle>
              <ListItemCtaVersion>{t('design.download.freeVersion')}</ListItemCtaVersion>
              <ListItemCtaIcon>
                <Disk width={15} height={22} color="var(--theme-palette-white)" />
              </ListItemCtaIcon>
            </ListItemCta>
          </ListItem>
        );
      })}
    </List>
  ) : null;
};
